import { useTranslation } from 'react-i18next'
import { Loading, Result } from '@components'
import {
  ONE_MOMENT,
  PROCESSING_SECURE_TRANSACTION
} from '@constants/translationsKeys'
import { CommonAuthProcessProps } from '@models'
import { AuthenticationProvider, AuthStep } from '@enums'
import { NotificationForm } from '@forms'
import { getAuthenticationStatusString } from '@utils/authentication-helpers'
import { useAuthProcess } from '../../hooks'
import { SetupForm, StepUpForm } from './forms'

const CybersourceAuth: React.FC<CommonAuthProcessProps> = ({
  showBrand,
  initialAuthentication
}) => {
  const { t } = useTranslation()

  const {
    isLoading,
    authStep,
    authentication,
    setupData,
    stepUpData,
    getStepUpPayerParams,
    checkAuthenticationResult
  } = useAuthProcess({
    authProvider: AuthenticationProvider.CYBERSOURCE,
    initialAuthentication,
    showBrand
  })

  const renderLoading = () => (
    <Loading
      showBrand={showBrand}
      message={t(ONE_MOMENT, { extra: '...' })}
      description={t(PROCESSING_SECURE_TRANSACTION, { extra: '...' })}
    />
  )

  const renderSetup = () => (
    <>
      {renderLoading()}
      <SetupForm
        accessToken={setupData?.accessToken ?? ''}
        collectionUrl={setupData?.collectionUrl ?? ''}
        onComplete={getStepUpPayerParams}
      />
    </>
  )

  const renderStepUp = () => (
    <StepUpForm
      stepUpUrl={stepUpData?.stepUpUrl ?? ''}
      accessToken={stepUpData?.accessToken ?? ''}
      onComplete={checkAuthenticationResult}
    />
  )

  const renderNotification = () => {
    return (
      <>
        {renderLoading()}
        <NotificationForm
          status={getAuthenticationStatusString(authentication?.status)}
          message={authentication?.statusMsg ?? ''}
          authenticationId={authentication?.authenticationId ?? ''}
          orderId={authentication?.orderId ?? ''}
          orderAmount={authentication?.orderAmount ?? 0}
          redirectUrl={authentication?.externalReturnUrl ?? ''}
        />
      </>
    )
  }

  const renderResult = () => (
    <Result
      authStatus={authentication?.status}
      showBrand={showBrand}
      message={authentication?.statusMsg ?? ''}
    />
  )

  if (isLoading) return renderLoading()

  const stepComponents: Partial<Record<AuthStep, () => JSX.Element>> = {
    [AuthStep.SETUP]: renderSetup,
    [AuthStep.STEP_UP]: renderStepUp,
    [AuthStep.NOTIFICATION]: renderNotification,
    [AuthStep.RESULT]: renderResult
  }

  return stepComponents[authStep]?.() ?? renderLoading()
}

export default CybersourceAuth
